import { useCallback } from 'react'
import { uniq, reject } from 'lodash-es'
import { resolveSelectedElementType } from '@editor/utils'
import {
  isSubSelectableInMode,
  useControlStore,
  useModelStore,
  useSelectionStore,
  useTapelineStore,
  roofStoreyKey,
} from '@editorStores'

const emptyElements: string[] = []

/** Modes that only make sense in the 3D view and are left when entering a storey (2D). */
const modesNotAvailableIn2D: StructuralPlanningModes[] = [
  'outer_walls',
  'inner_walls',
  'multi-edit',
]

/** Floorplan-only modes that are left when leaving the storey — kept active, the 3D scene would
 *  fall back to the storey-selection screen instead of the overview. */
const modesNotAvailableIn3D: StructuralPlanningModes[] = ['draw-walls', 'draw-columns']

/**
 * Selection/mode state of the Structural Planning page (formerly `useStructuralPlanningQueryParams`
 * when it lived in query params), backed by the global selection store so the selection survives
 * page and floorplan/3D switches. Every transition goes through the store's reconciler, which
 * keeps the selection exactly when the target mode can still hold it (see
 * `selectionStore/modeSpecs.ts`).
 */
const useStructuralPlanningSelection = () => {
  const model = useModelStore(state => state.model)

  const isTapelineActive = useTapelineStore(state => state.isActive)

  const storey = useControlStore(state => state.floorplanState.storey)
  const setFloorplanStorey = useControlStore(state => state.setFloorplanStorey)

  const selection = useSelectionStore(state => state.selection)
  const subSelection = useSelectionStore(state => state.subSelection)
  const mode = useSelectionStore(state => state.mode)
  const activeTab = useSelectionStore(state => state.activeTab)
  const drawAreaLoadElement = useSelectionStore(state => state.drawAreaLoadElement)
  const select = useSelectionStore(state => state.select)
  const selectSub = useSelectionStore(state => state.selectSub)
  const switchMode = useSelectionStore(state => state.switchMode)
  const setStoreActiveTab = useSelectionStore(state => state.setActiveTab)
  const setStoreDrawAreaLoadElement = useSelectionStore(state => state.setDrawAreaLoadElement)

  const modeTyped = (mode === 'neutral' ? null : mode) as StructuralPlanningModes | null

  const selectedElements = selection?.type === 'planar' ? selection.guids : emptyElements
  const selectedStiffeningElements =
    subSelection?.type === 'stiffening' ? [subSelection.guid] : undefined
  const connector = subSelection?.type === 'connector' ? subSelection.guid : undefined
  const verticalTransmitter =
    subSelection?.type === 'vertical-transmitter' ? subSelection.guid : undefined
  const load = subSelection?.type === 'load' ? subSelection.guid : undefined

  const getSelectedElementTabType = useCallback(
    (elementGuid: string) => resolveSelectedElementType(model, elementGuid),
    [model],
  )

  const toPlanarSelection = useCallback(
    (
      elements?: string[] | null,
      elementType?: PlanarSelectedElementType,
    ): PlanarElementSelection | null =>
      elements?.length
        ? {
            type: 'planar',
            elementType: elementType ?? getSelectedElementTabType(elements[0]),
            guids: elements as string[],
          }
        : null,
    [getSelectedElementTabType],
  )

  /** Close the current selection: clears selection + sub-selection and returns to neutral. */
  const reset = useCallback(() => {
    select(null, { mode: 'neutral', activeTab: null })
  }, [select])

  /** Toggle a mode: same/empty mode goes back to neutral; the selection survives when the target
   *  mode can still hold it. */
  const toggleMode = useCallback(
    (newMode?: StructuralPlanningModes) => {
      if (!newMode || mode === newMode) switchMode('neutral')
      else switchMode(newMode as EditorMode)
    },
    [mode, switchMode],
  )

  const toggleLoadTypeMode = useCallback(
    (newMode: 'line-load' | 'point-load' | 'area-load') => {
      switchMode(newMode)
    },
    [switchMode],
  )

  const setMode = useCallback(
    (newMode: StructuralPlanningModes) => {
      switchMode((newMode ?? 'neutral') as EditorMode)
    },
    [switchMode],
  )

  const setElements = useCallback(
    (elements: string[], elementType?: ElementTypes | 'rip' | 'lintel' | 'opening') => {
      if (!elements.length) {
        // Toggle-off (e.g. clicking the already selected element): back to neutral, keep the tab.
        select(null, { mode: 'neutral' })
        return
      }

      select(toPlanarSelection(elements, elementType), { adopt: 'owning' })
    },
    [select, toPlanarSelection],
  )

  /** Select an element for the load-application tool: unlike `setElements`, this keeps the current
   *  load mode instead of adopting the element's own owning mode (the load modes already accept
   *  any planar type — see `modeSpecs.ts`). */
  const setLoadElement = useCallback(
    (elements: string[]) => {
      if (!elements.length) {
        select(null, { mode: 'neutral' })
        return
      }

      select(toPlanarSelection(elements), { adopt: 'if-needed' })
    },
    [select, toPlanarSelection],
  )

  const multiSelectElements = useCallback(
    (elementGuids: string[]) => {
      select(
        { type: 'planar', guids: uniq([...selectedElements, ...elementGuids]), isMulti: true },
        { mode: 'multi-edit', activeTab: null },
      )
    },
    [select, selectedElements],
  )

  /** Replace the whole multi-selection (unlike multiSelectElements, which appends). Used by the
   *  element list pane, where each click yields the complete next selection. */
  const setMultiSelection = useCallback(
    (elementGuids: string[]) => {
      select(elementGuids.length ? { type: 'planar', guids: elementGuids, isMulti: true } : null, {
        mode: 'multi-edit',
        activeTab: null,
      })
    },
    [select],
  )

  const removeMultiSelectElement = useCallback(
    (elementGuid: string) => {
      const newElements = reject(selectedElements, element => element === elementGuid)

      select(newElements.length ? { type: 'planar', guids: newElements, isMulti: true } : null, {
        mode: 'multi-edit',
        activeTab: null,
      })
    },
    [select, selectedElements],
  )

  const resetElements = useCallback(() => {
    select(null)
  }, [select])

  const setStiffeningElement = useCallback(
    (stiffeningElementGuid: string) => {
      selectSub(
        {
          type: 'stiffening',
          guid: stiffeningElementGuid,
          elementGuid: selectedElements[0] ?? null,
        },
        { activeTab: 'geometry' },
      )
    },
    [selectSub, selectedElements],
  )

  const setConnector = useCallback(
    (connectorGuid: string, elementGuid: string) => {
      if (isTapelineActive) {
        return
      }

      const sub: SceneElementSubSelection = { type: 'connector', guid: connectorGuid, elementGuid }

      // Another transmitter of the element already selected only changes the sub-selection: the
      // mode stays as it is (e.g. neutral after returning from another page), so this stays within
      // the pane even while a form there refuses leaving it
      if (
        selectedElements.length === 1 &&
        selectedElements[0] === elementGuid &&
        isSubSelectableInMode(mode, sub)
      ) {
        selectSub(sub, { activeTab: 'connector' })
        return
      }

      const type = getSelectedElementTabType(elementGuid)

      select(
        { type: 'planar', elementType: type, guids: [elementGuid] },
        { adopt: 'owning', sub, activeTab: 'connector' },
      )
    },
    [select, selectSub, selectedElements, mode, getSelectedElementTabType, isTapelineActive],
  )

  const setVerticalTransmitter = useCallback(
    (transmitterGuid: string, elementGuid: string, transmitterOnly = false) => {
      if (isTapelineActive) {
        return
      }
      if (transmitterOnly) {
        selectSub({ type: 'vertical-transmitter', guid: transmitterGuid, elementGuid })

        return
      }

      const type = getSelectedElementTabType(elementGuid)

      select(
        { type: 'planar', elementType: type, guids: [elementGuid] },
        {
          adopt: 'owning',
          sub: { type: 'vertical-transmitter', guid: transmitterGuid, elementGuid },
          activeTab: 'vertical-transmission',
        },
      )
    },
    [select, getSelectedElementTabType, isTapelineActive, selectSub],
  )

  const setActiveTab = useCallback(
    (newActiveTab: string) => {
      setStoreActiveTab(newActiveTab)
    },
    [setStoreActiveTab],
  )

  const setStorey = useCallback(
    (newStorey: string | null) => {
      if (newStorey === roofStoreyKey) return

      setFloorplanStorey(newStorey)

      // Selection is kept across 2D/3D switches; only modes that don't exist in the target view
      // are left (the reconciler decides what survives the switch to neutral).
      if (mode === 'storey-selection') switchMode('neutral')
      else if (
        newStorey !== null &&
        modesNotAvailableIn2D.includes(mode as StructuralPlanningModes)
      )
        switchMode('neutral')
      else if (
        newStorey === null &&
        modesNotAvailableIn3D.includes(mode as StructuralPlanningModes)
      )
        switchMode('neutral')
    },
    [mode, switchMode, setFloorplanStorey],
  )

  const selectLoad = useCallback(
    (loadToSelect: ElementLoad) => {
      select(toPlanarSelection([loadToSelect.element_guid]), {
        mode: loadToSelect.load_type,
        sub: {
          type: 'load',
          guid: loadToSelect.guid,
          elementGuid: loadToSelect.element_guid,
        },
        activeTab: null,
      })
    },
    [select, toPlanarSelection],
  )

  /** Clear the load sub-selection (e.g. when the currently sub-selected load is deleted) so the
   *  ESC ladder doesn't keep consuming a step on a sub-selection pointing at nothing. */
  const clearLoadSelection = useCallback(() => {
    selectSub(null)
  }, [selectSub])

  const setDrawAreaLoadElement = useCallback(
    (newDrawAreaLoadElement?: string) => {
      switchMode('area-load')
      setStoreDrawAreaLoadElement(newDrawAreaLoadElement ?? null)
    },
    [switchMode, setStoreDrawAreaLoadElement],
  )

  return {
    modes: {
      isStiffeningSelectionMode: modeTyped === 'selection',
      isBuildingMode: modeTyped === 'building',
      isRoofSlabMode: modeTyped === 'roof_slabs' || modeTyped === 'slabs',
      isVerticalSlabMode: modeTyped === 'vertical_slabs',
      isBeamMode: modeTyped === 'beams',
      isRoofMode: modeTyped === 'vertical_roof_slabs',
      isColumnMode: modeTyped === 'columns',
      isPurlinMode: modeTyped === 'purlins',
      /**
       * A wall has been selected (either inner or outer wall)
       */
      isWallMode: modeTyped === 'inner_walls' || modeTyped === 'outer_walls',
      isPositionMode: modeTyped === 'position',
      isStiffeningMode: modeTyped === 'stiffening',
      isLoadMode:
        modeTyped === 'line-load' || modeTyped === 'point-load' || modeTyped === 'area-load',
      /**
       * No mode has been selected and we are in global and not floorplan mode
       * (no storey has been selected)
       */
      isNeutralMode: !storey && !modeTyped,
      isDrawingBeamsMode: modeTyped === 'draw-beams',
      isDrawingColumnsMode: modeTyped === 'draw-columns',
      isDrawingPurlinsMode: modeTyped === 'draw-purlins',
      isDrawingVerticalSlabsMode: modeTyped === 'draw-vertical-slabs',
      isDrawingVerticalRoofMode: modeTyped === 'draw-vertical-roofs',
      isDrawingSlabMode: modeTyped === 'draw-slabs',
      isDrawingRoofMode: modeTyped === 'draw-roofs',
      isDrawingWallsMode: modeTyped === 'draw-walls',
      isDrawingOpeningsMode: modeTyped === 'draw-openings',
      isEditOpenings3DMode: modeTyped === 'edit-openings-3d',
      isStoreySelectionMode: modeTyped === 'storey-selection',
      isDrawMode:
        modeTyped === 'draw-vertical-slabs' ||
        modeTyped === 'draw-beams' ||
        modeTyped === 'draw-columns' ||
        modeTyped === 'draw-walls' ||
        modeTyped === 'draw-openings' ||
        modeTyped === 'draw-vertical-roofs' ||
        modeTyped === 'draw-purlins' ||
        modeTyped === 'wall-rip',
      isRipMode: modeTyped === 'rip',
      isWallRipMode: modeTyped === 'wall-rip',
      isWallLintelMode: modeTyped === 'wall-lintel',
      isLintelMode: modeTyped === 'lintel',
      isFoundationMode: modeTyped === 'foundation',
      isMultiEdit: modeTyped === 'multi-edit',
      isOpeningsMode: modeTyped === 'openings',
      isCommentsMode: modeTyped === 'comments',
      isPlanTableMode: modeTyped === 'plan-table',
      isPlanImageCardMode: modeTyped === 'plan-image-card',
    },
    params: {
      mode: modeTyped,
      selectedElements,
      selectedStiffeningElements,
      connectorGuid: connector,
      activeTab,

      load,
      verticalTransmitter,

      loadType:
        modeTyped === 'line-load' || modeTyped === 'point-load' || modeTyped === 'area-load'
          ? modeTyped
          : undefined,
      drawAreaLoadElement,
      storey,
    },
    actions: {
      resetElements,
      toggleMode,
      toggleLoadTypeMode,
      setMode,
      setElements,
      setLoadElement,
      setStiffeningElement,
      setVerticalTransmitter,
      setConnector,
      setActiveTab,
      selectLoad,
      clearLoadSelection,
      setDrawAreaLoadElement,
      reset,
      setStorey,
      multiSelectElements,
      setMultiSelection,
      removeMultiSelectElement,
    },
  }
}

export default useStructuralPlanningSelection
